import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/shopify-checkout-optimieren";
const TITLE = "Shopify Checkout optimieren: Kaufabbrüche systematisch senken";
const DESCRIPTION =
  "Checkout Optimierung für Shopify: die häufigsten Abbruchgründe, konkrete Maßnahmen für Warenkorb, Versandkosten, Zahlarten und Mobile, plus saubere Messung des Effekts.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Die meisten Checkout-Abbrüche entstehen vor dem Checkout: durch Versandkosten, die erst spät sichtbar werden, fehlende Lieferzeitangaben, zu viele Pflichtfelder und fehlende Wunsch-Zahlarten. Wer Versandkosten, Lieferzeit und Zahlarten bereits im Warenkorb zeigt, Express-Zahlarten mobil nach oben holt und Gastbestellungen erlaubt, senkt die Abbruchquote am schnellsten. Messen Sie den Effekt getrennt nach Mobile und Desktop je Checkout-Schritt.";

const faqs = [
  {
    q: "Warum brechen so viele Kunden im Checkout ab?",
    a: "Die beiden Hauptgründe sind unerwartete Kosten und Aufwand. Versandkosten, die erst im letzten Schritt auftauchen, und Formulare mit zu vielen Pflichtfeldern kosten mehr Umsatz als jedes Designdetail.",
  },
  {
    q: "Kann man den Shopify Checkout überhaupt anpassen?",
    a: "Ja, in Grenzen. Über Checkout-Einstellungen, Versand- und Zahlartenlogik, Express-Checkout sowie, bei Shopify Plus, über Checkout Extensions lässt sich einiges steuern. Der größte Hebel liegt ohnehin davor: im Warenkorb.",
  },
  {
    q: "Welche Zahlarten sollte ein deutscher Shop anbieten?",
    a: "In deutschen Shops sind PayPal, Kauf auf Rechnung, Kreditkarte, Lastschrift und Apple Pay oder Google Pay die üblichen Erwartungen. Fehlt die bevorzugte Zahlart, bricht ein Teil der Kunden im letzten Schritt ab.",
  },
  {
    q: "Bringt ein One-Page-Checkout mehr?",
    a: "Meist ja, weil weniger Schritte weniger Absprungpunkte bedeuten. Entscheidend ist aber nicht die Zahl der Seiten, sondern wie viele Entscheidungen und Eingaben Sie dem Kunden abverlangen.",
  },
  {
    q: "Wie messe ich die Wirkung der Checkout-Optimierung?",
    a: "Vergleichen Sie die Abbruchquote je Checkout-Schritt sowie die Kaufabschlussrate getrennt nach Mobile und Desktop über mindestens zwei bis vier Wochen vor und nach der Änderung. Ohne diese Trennung überlagern saisonale Effekte das Ergebnis.",
  },
];

export const Route = createFileRoute("/blog/shopify-checkout-optimieren")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Checkout Optimierung",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · Checkout"
      title="Shopify Checkout optimieren: Hebel gegen Kaufabbrüche"
      intro="Im Checkout stehen Kunden, die bereits kaufen wollten. Jeder Abbruch hier ist teurer als ein verlorener Besucher auf der Startseite, und meistens auf wenige, immer gleiche Ursachen zurückzuführen."
      source="blog-checkout"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/blog", label: "Blog" }}
      sections={[
        {
          h: "Die Abbruchgründe, die wirklich zählen",
          p: [
            "In der Praxis lassen sich fast alle Checkout-Verluste auf fünf Ursachen zurückführen. Wer diese abstellt, holt den Großteil des möglichen Umsatzes, bevor überhaupt getestet wird.",
          ],
          list: [
            "Versandkosten erscheinen erst im letzten Schritt",
            "Keine Angabe zur Lieferzeit",
            "Zu viele Pflichtfelder, kein Autofill",
            "Fehlende Wunsch-Zahlart (Rechnung, PayPal, Apple Pay)",
            "Kein Vertrauenssignal zu Rückgabe und Sicherheit",
          ],
        },
        {
          h: "Der Warenkorb ist Teil des Checkouts",
          p: [
            "Der günstigste Ort, um Erwartungen zu setzen, ist der Warenkorb. Dort gehören Versandkosten, Lieferzeit, Rückgaberecht und die verfügbaren Zahlarten hin, sichtbar, ohne Klick.",
            "Ein Fortschrittshinweis zur versandkostenfreien Grenze erhöht zusätzlich den Warenkorbwert, wenn die Grenze realistisch über dem aktuellen Durchschnitt liegt.",
          ],
        },
        {
          h: "Zahlarten und Vertrauen",
          p: [
            "Zahlarten entscheiden im letzten Moment. Fehlt Kauf auf Rechnung im deutschen Markt oder PayPal bei Erstkäufern, bricht ein Teil der Kunden ab, obwohl der Warenkorb bereits gefüllt war.",
            "Vertrauenssignale gehören dorthin, wo die Unsicherheit entsteht: Rückgabefrist und Kontaktmöglichkeit direkt neben dem Bestellbutton statt versteckt im Footer.",
          ],
        },
        {
          h: "Mobile zuerst",
          p: [
            "Der Großteil des Traffics ist mobil, der Großteil der Abbrüche auch. Express-Zahlarten direkt über dem Formular, große Touch-Ziele, numerische Tastaturen für Postleitzahl und Kartennummer sowie ein Verzicht auf Pflicht-Accounts sind hier die schnellsten Gewinne.",
          ],
        },
        {
          h: "Nach dem Abbruch: Rückholung",
          p: [
            "Abgebrochene Warenkörbe sind kein verlorener Umsatz, sondern eine zweite Chance. Eine erste Erinnerung nach etwa einer Stunde, eine zweite nach 24 Stunden, ohne Rabatt in der ersten Mail, sonst erzieht man Kunden zum Warten.",
          ],
        },
        {
          h: "Erfolg messen statt hoffen",
          p: [
            "Messen Sie die Abbruchquote pro Checkout-Schritt vor und nach der Änderung, getrennt nach Mobile und Desktop. Nur so lässt sich sagen, welche Maßnahme gewirkt hat und welche nur besser aussah.",
            "Für die Umsetzung im Theme rechne ich mit 110 € netto pro Stunde, ein vollständiges Conversion-Projekt startet ab 3.990 € netto.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung anfragen" },
        { to: "/ratgeber/shopify-warenkorbabbrueche-reduzieren", label: "Warenkorbabbrüche reduzieren" },
        { to: "/ratgeber/shopify-produktseite-optimieren", label: "Produktseite optimieren" },
        { to: "/blog/shopify-zahlungsarten-deutschland", label: "Zahlungsarten in Deutschland" },
        { to: "/kontakt", label: "Checkout prüfen lassen" },
      ]}
    />
  );
}
